/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import {
    IBuyboxAddToCartViewProps,
    IBuyboxAddToOrderTemplateViewProps,
    IBuyboxAddToWishlistViewProps,
    IBuyboxCallbacks,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxShopSimilarLookViewProps,
    IBuyboxState
} from '@msdyn365-commerce-modules/buybox';
import {
    IHeiBuyboxData as IBuyboxData,
    IBuyboxFindInStoreViewProps,
    IHeiBuyboxProps as IBuyboxProps,
    IBuyboxViewProps
} from '../../../modules/hei-buybox';
import { IncrementalQuantity, ITelemetryContent, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { HtmlHeadInclude, IRequestContext } from '@msdyn365-commerce/core-internal';

import {
    IHeiBuyboxProps as IBuyboxExtentionProps,
    IHeiBuyboxResources as IBuyboxExtentionResources
} from '../definition-extensions/hei-buybox.ext.props.autogenerated';
import * as Msdyn365 from '@msdyn365-commerce/core';
import {
    getApplicableWarrantiesAsync,
    getAttributeValuesAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

import { AsyncResult, SimpleProduct, WarrantyGroup } from '@msdyn365-commerce/retail-proxy';
import { IWarrantySelectionResources, WarrantySelection } from './components/WarrantySelection';
import Bazzarvoice from '../../../modules/bazzarvoice-widget/components/Bazzarvoice';
import { widgetType as WidgetType } from '../../../modules/bazzarvoice-widget/bazzarvoice-widget.props.autogenerated';
import { canUseDOM, ICustomWindow } from '../utilities/helpers';
import { findAttribute, findAttributeValue } from '../utilities/productListExtensionHelpers';
import { generateImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import { GetCookie, SetCookie } from '../../../shared-utils/cookie-utilities';
import AddThisShare from './components/add-this-share';
import { IMfrCodeClass } from '../../../actions/DataServiceEntities.g';
import { getMfrCodeAsync } from '../../../actions/DataActionExtension.g';

import { IProductPromotion } from '../../../actions/product-services';
import { getProductPromotionsAsync } from '../../../actions/product-services/DataActionExtension.g';

const BuyboxView: React.FC<IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>> = props => {
    const {
        ModuleProps,
        MediaGalleryContainerProps,
        ProductInfoContainerProps,
        addToCart,
        addToOrderTemplate,
        addToWishlist,
        telemetryContent,
        configure,
        description,
        findInStore,
        quantity,
        price,
        title,
        callbacks,
        state,
        resources,
        inventoryLabel,
        shopSimilarLook,
        keyInPrice,
        quantityLimitsMessages,
        max,
        shopSimilarDescription,
        unitOfMeasure
    } = props;

    const {
        data: { product, productPrice },
        context: { request }
    } = props;
    // @ts-ignore;
    const mfrCodeData: AsyncResult<IMfrCodeClass> | undefined = props.data?.mfrCode;
    const [warrantyGroups, setWarrantyGroups] = React.useState<WarrantyGroup[]>([]);
    const [hidePrice, setHidePrice] = React.useState<boolean | null>(null);
    const [selectedWarranty, setSelectedWarranty] = React.useState<SimpleProduct | undefined>(undefined);
    const [mfrCode, setMfrCode] = React.useState<string | undefined>(mfrCodeData?.result?.ExternalItemId);
    const [brand, setBrand] = React.useState('');
    const [productPromo, setProductPromo] = React.useState<IProductPromotion[]>([]);
    const actionContext = props.context.actionContext;
    const recordId = props.context.actionContext.requestContext.urlTokens.recordId;

    const catalogId = props.context.actionContext.requestContext.apiSettings.catalogId;
    const channelId = props.context.actionContext.requestContext.apiSettings.channelId;
    if (props.config.trackRecentlyViewedItems) {
        _updateRecentlyViewedItemsCookie(props);
    }

    const getProductAttributes = () => {
        getAttributeValuesAsync({ callerContext: actionContext }, Number(recordId), channelId, catalogId).then(data => {
            const hidePriceAttribute = findAttribute(data, 'See Price In Cart');
            if (hidePriceAttribute) {
                const hidePriceValue = findAttributeValue(hidePriceAttribute);
                if (typeof hidePriceValue === 'boolean' && hidePriceValue) {
                    setHidePrice(hidePriceValue);
                } else {
                    setHidePrice(false);
                }
            } else {
                setHidePrice(false);
            }

            const bandAttribute = findAttribute(data, 'Brand') || findAttribute(data, 'Marque');
            setBrand(bandAttribute?.TextValue || '');
        });
    };
    React.useEffect(() => {
        if (recordId) {
            _getProductWarranties(actionContext, catalogId, [Number(recordId)], setWarrantyGroups);
            getProductAttributes();
        }
    }, []);

    const updateSelectedWarranty = (warranty: SimpleProduct | undefined) => {
        if (canUseDOM()) {
            const customWindow: ICustomWindow = window;
            customWindow.__warranty__ = warranty;
        }
        setSelectedWarranty(warranty);
    };

    const productInfo = product.result;
    const itemId = productInfo?.ItemId;

    const hasSerialNumber = productInfo?.Behavior?.HasSerialNumber || false;

    const masterProductId = productInfo?.MasterProductId ? productInfo?.MasterProductId : 0;

    React.useEffect(() => {
        if (recordId && productInfo && productInfo?.RecordId !== +recordId) getMfrCode(productInfo.RecordId);
        if (recordId && productInfo && productInfo?.RecordId !== +recordId) getProductPromo(masterProductId, productInfo.RecordId);
    }, [productInfo]);

    const getProductPromo = async (recordId: number, recordvariantId: number) => {
        var productPromotionsInput = await getProductPromotionsAsync(
            { callerContext: props.context.actionContext },
            recordId,
            recordvariantId
        );
        //   console.log('Called getProductPromotionsAsync ' + ' recordId : ' + recordId + ' VariantId : ' + recordvariantId);

        if (productPromotionsInput) {
            setProductPromo(productPromotionsInput);
        }
    };

    const getMfrCode = async (recordId: number) => {
        const data = await getMfrCodeAsync({ callerContext: props.context.actionContext }, recordId);
        if (data) {
            setMfrCode(data.ExternalItemId);
        }
    };

    const _getWarrantyResources = (): IWarrantySelectionResources => {
        const { productProtectionLabel, noPlanSelectedLabel, dayLabel, monthLabel, yearLabel } = props.resources;
        return {
            dayLabel,
            monthLabel,
            noPlanSelectedLabel,
            yearLabel,
            productProtectionLabel
        };
    };

    React.useEffect(() => {
        if (productInfo) {
            const gtmProduct: any = {
                name: productInfo.Name,
                id: itemId,
                price: productPrice?.result?.CustomerContextualPrice ?? productInfo.Price
            };

            if (brand) {
                gtmProduct.brand = brand;
            }

            window['heiGtmProduct'] = gtmProduct;
            process.env.NODE_ENV === 'development' &&
                console.log('heiGtmProduct', JSON.parse(JSON.stringify(productInfo)), JSON.parse(JSON.stringify(productPrice?.result ?? {})));
        }
    }, [productInfo, brand]);

    return (
        <Module {...ModuleProps}>
            <div className='buybox-title-container'>
                {title} {_renderAuthorizedDealerBadge(brand, props.context.request)}
            </div>
            {itemId && (
                <span>
                    {resources.webCodeLabel}: {itemId}{' '}
                    {mfrCode ? `${String.fromCharCode(183)} ${resources.mfrCodeLabel}: ${mfrCode}` : null}
                </span>
            )}
            <div className='buybox-content'>
                <Node {...MediaGalleryContainerProps}>{props.slots.mediaVideoGallery || props.mediaGallery}</Node>
                <Node {...ProductInfoContainerProps}>
                    {hidePrice === null ? null : hidePrice ? ( // Stop price rendering server side
                        <p className={'hide-price hide-price--detail'}>{resources.seePriceInCartLabel}</p>
                    ) : (
                        price
                    )}
                    {productInfo && _renderBazzarvoiceRating(productInfo)}
                    {unitOfMeasure}
                    {description}
                    {_renderTextBlock(props.slots.textBlocks)}
                    {_renderPromotions(props, productPromo)}
                    {configure && _renderConfigure(configure)}
                    {warrantyGroups && (
                        <WarrantySelection
                            warrantyGroups={warrantyGroups}
                            formatPriceFn={props.context.cultureFormatter.formatCurrency}
                            selectedWarranty={selectedWarranty?.RecordId || 0}
                            setSelectedWarranty={updateSelectedWarranty}
                            resources={_getWarrantyResources()}
                        />
                    )}
                    {keyInPrice && _renderKeyInPrice(keyInPrice)}
                    {quantity &&
                        !hasSerialNumber &&
                        _renderQuantity(
                            quantity,
                            callbacks,
                            props,
                            state,
                            resources,
                            quantityLimitsMessages,
                            max,
                            !!selectedWarranty,
                            telemetryContent
                        )}
                    {inventoryLabel}
                    {_renderCartAndActions(addToCart, addToOrderTemplate, addToWishlist)}
                    {findInStore && _renderFindInStore(findInStore)}
                    {_renderSocialShare(props.slots && props.slots?.socialShare)}
                    {shopSimilarLook && _renderShopSimilarItem(shopSimilarLook)}
                    {shopSimilarDescription && _renderShopSimilarItem(shopSimilarDescription)}
                </Node>
            </div>
            {productInfo && _renderJsonLinkingData(productInfo, brand, request)}
        </Module>
    );
};

const _renderJsonLinkingData = (productInfo: SimpleProduct, brand: string, request: IRequestContext) => {
    const ld = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        '@id': request.canonicalUrl || request.url.requestUrl,
        name: productInfo.Name || '',
        image: productInfo.PrimaryImageUrl,
        description: productInfo.Description,
        sku: productInfo.ItemId,
        brand: brand
    };

    return (
        <HtmlHeadInclude>
            {/* tslint:disable-next-line: react-no-dangerous-html */}
            <script
                data-load-point='headEnd'
                type='application/ld+json'
                id='hei-buybox__ld-json'
                dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }}
            />
        </HtmlHeadInclude>
    );
};

const _renderBazzarvoiceRating = (productInfo: SimpleProduct) => {
    return (
        <div className='hei-bazzarvoice-rating'>
            <Bazzarvoice productInfo={productInfo} widgetType={WidgetType.rating_summary} />
        </div>
    );
};

const _renderTextBlock = (textBlocks: React.ReactNode[]): JSX.Element | undefined => {
    if (!textBlocks || textBlocks.length === 0) {
        return undefined;
    }

    return <>{textBlocks[0]}</>;
};

const _renderSocialShare = (socialShare: React.ReactNode[]): JSX.Element | undefined => {
    if (!socialShare || socialShare.length === 0) {
        return undefined;
    }

    return (
        <div>
            {socialShare[0]}
            <AddThisShare />
        </div>
    );
};

const _renderCartAndActions = (
    addToCart?: IBuyboxAddToCartViewProps,
    addToOrderTemplate?: IBuyboxAddToOrderTemplateViewProps,
    addToWishlist?: IBuyboxAddToWishlistViewProps
): JSX.Element | undefined => {
    if (!addToCart && !addToWishlist) {
        return undefined;
    }
    return (
        <div className='product-add-to-cart'>
            {addToCart && addToCart.errorBlock}
            {addToOrderTemplate && addToOrderTemplate.errorBlock}
            {addToWishlist && addToWishlist.errorBlock}
            <div className='buttons'>
                {addToCart && addToCart.button}
                {addToOrderTemplate && <Node {...addToOrderTemplate.ContainerProps}>{addToOrderTemplate.button}</Node>}
                {addToWishlist && <Node {...addToWishlist.ContainerProps}>{addToWishlist.button}</Node>}
            </div>
        </div>
    );
};

const _getProductWarranties = (
    ctx: Msdyn365.IActionContext,
    id: number,
    productIds: number[],
    setWarrantyGroups: React.Dispatch<React.SetStateAction<WarrantyGroup[]>>
): void => {
    getApplicableWarrantiesAsync({ callerContext: ctx }, id, productIds).then(data => {
        if (data) {
            const productHasWarranties = !!data[0].WarrantyGroups?.length;
            if (productHasWarranties) {
                setWarrantyGroups(data[0].WarrantyGroups || []);
            }
        }
    });
};

const _renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(_renderConfigureDropdown)}</Node>;
};

const _renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>
            {select}
        </Node>
    );
};

const _renderFindInStore = (findInStore: IBuyboxFindInStoreViewProps): JSX.Element => {
    const { ContainerProps, storeSelector, heading, description, errors, button, modal, productPickupOptionList } = findInStore;

    return (
        <Node {...ContainerProps}>
            {storeSelector}
            {heading}
            {productPickupOptionList}
            {description}
            {errors}
            {button}
            {modal}
        </Node>
    );
};

/**
 * On Change function.
 * @param callbacks -Callbacks.
 * @returns Update quantity.
 */
const onChangeHandler = (callbacks: IBuyboxCallbacks) => (newValue: number): boolean => {
    if (callbacks.updateQuantity) {
        return callbacks.updateQuantity(newValue);
    }
    return true;
};

const _renderQuantity = (
    quantityComponent: IBuyboxProductQuantityViewProps,
    callbacks: IBuyboxCallbacks,
    props: IBuyboxProps<IBuyboxData>,
    state: IBuyboxState,
    extentionResources: IBuyboxExtentionResources,
    quantityLimitsMessages: React.ReactNode,
    max: number | undefined,
    warrantyIsSelected: boolean,
    telemetryContent?: ITelemetryContent
): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors } = quantityComponent;

    const { resources } = props;

    const { quantity } = state;

    if (warrantyIsSelected && quantity > 1) {
        callbacks.updateQuantity(1);
    }

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>

            <IncrementalQuantity
                id='ms-buybox__product-quantity-input'
                max={warrantyIsSelected ? 1 : max || 10}
                currentCount={quantity}
                onChange={onChangeHandler(callbacks)}
                inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                decrementButtonAriaLabel={extentionResources.decrementButtonAriaLabel}
                incrementButtonAriaLabel={extentionResources.incrementButtonAriaLabel}
                minQuantityText={extentionResources.minQuantityText}
                maxQuantityText={extentionResources.maxQuantityText}
                telemetryContent={telemetryContent}
            />
            {quantityLimitsMessages}
        </Node>
    );
};

const _renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

const _renderShopSimilarItem = (shopSimilarItem: IBuyboxShopSimilarLookViewProps): JSX.Element => {
    const { ContainerProps, errors, input } = shopSimilarItem;

    return (
        <Node {...ContainerProps}>
            {errors}
            {input}
        </Node>
    );
};

const _renderPromotions = (props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>, promo: IProductPromotion[]) => {
    const {
        context: { request },
    } = props;

    const sitePath = request.sitePath;

    var offers = promo?.filter(o => o.OfferId);
    if (offers && offers.length > 0) {
        return (
            <ul className='hei-buybox-promotion-list'>
                {offers.map(o => {
                    return (
                        <li key={o.OfferId} className='hei-buybox-promotion-item' data-offer={o.OfferId}>
                            <a href={`${sitePath}/promotion/${o.OfferId?.toLowerCase()}`}>{o.Name}</a>
                        </li>
                    );
                })}
            </ul>
        );
    }

    return null;
};

const _renderAuthorizedDealerBadge = (brand: string, requestContext: Msdyn365.IRequestContext) => {
    const locale = requestContext.locale;
    if (brand && requestContext.gridSettings) {
        const fileName = `Catalogs/${locale}/${toCamelCase(brand)}_AuthDLR_${locale}.png`;
        const categoryImageUrl = generateImageUrl(fileName, requestContext.apiSettings);

        if (categoryImageUrl) {
            const ImageData: Msdyn365.IImageData = { src: categoryImageUrl };
            return (
                <div className='hei-buybox-wrapper-auth-dlr'>
                    <Msdyn365.Image
                        className='hei-buybox-image__item'
                        altText={brand}
                        {...ImageData}
                        gridSettings={requestContext.gridSettings}
                        loadFailureBehavior='hide'
                        role='banner'
                        id={`${fileName}__authDLR`}
                    />
                </div>
            );
        }
    }

    return null;
};

const _updateRecentlyViewedItemsCookie = (props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>): void => {
    const cookieName: string = '_msdyn365___recently_viewed_products';
    const cookieAge: number = 31536000000;
    const cookieValue = GetCookie(props.context.request.cookies, cookieName);
    let productIds = (cookieValue && cookieValue.split(',').map(x => +x)) || [];
    const recordId: number = (props.data.product.result && props.data.product.result.RecordId) || 0;
    const max: number = props.config.maxRecentlyViewedItemsCount || 10;
    if (recordId !== 0 && productIds.indexOf(recordId) === -1) {
        productIds.push(recordId);
    }
    if (productIds.length > max) {
        productIds = productIds.slice(Math.max(productIds.length - max, 0));
    }
    const newCookieValue = String(productIds);
    SetCookie(props.context.request.cookies, newCookieValue, cookieName, cookieAge);
};

const toCamelCase = (str: string) => {
    return str
        .replace(/\s(.)/g, function ($1) {
            return $1.toLowerCase();
        })
        .replace(/\s/g, '')
        .replace(/^(.)/, function ($1) {
            return $1.toUpperCase();
        });
};

export default BuyboxView;
